import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { PageLoad, ErrorMsg } from '../components/ui';
import './InboxPage.css';

const Ico = ({ d, size = 18, stroke = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {typeof d === 'string' ? <path d={d} /> : d}
  </svg>
);
const MailIcon  = ({ size = 18 }) => <Ico size={size} d={<g><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></g>} />;
const SendIcon  = ({ size = 12 }) => <Ico size={size} d="M22 2L11 13M22 2L15 22L11 13L2 9z" />;
const BackIcon  = ({ size = 16 }) => <Ico size={size} d="M15 18l-6-6 6-6" />;
const ReplyDown = ({ size = 11 }) => <Ico size={size} d={<g><polyline points="9 17 4 12 9 7" /><line x1="20" y1="12" x2="4" y2="12" /></g>} />;
const SearchIcon = () => <Ico size={16} d={<g><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></g>} />;
const UserIcon  = ({ size = 13 }) => <Ico size={size} d={<g><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></g>} />;
const ExpandIcon   = ({ size = 14 }) => <Ico size={size} d={<g><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></g>} />;
const CollapseIcon = ({ size = 14 }) => <Ico size={size} d={<g><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="10" y1="14" x2="3" y2="21"/><line x1="21" y1="3" x2="14" y2="10"/></g>} />;

// Strip repeated Re:/Fwd:/Fw: prefixes — mirrors the server's thread normalization
const baseSubject = (s) => (s || '').replace(/^((re|fwd|fw)\s*:\s*)+/i, '').trim();

// Split a plain-text email body into the new content and the quoted/forwarded tail.
// Returns { main, quoted } — quoted is null if nothing was detected.
function splitQuotedText(text) {
  if (!text) return { main: text, quoted: null };
  const lines = text.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();

    // Forwarded message divider
    if (/^-{5,}\s*forwarded message\s*-{5,}$/i.test(trimmed)) {
      return {
        main: lines.slice(0, i).join('\n').trimEnd() || null,
        quoted: lines.slice(i).join('\n').trim(),
      };
    }

    // "On [date] ... wrote:" — may span 1-4 lines
    if (/^on .+/i.test(trimmed)) {
      // Check this line or the next few for "wrote:"
      for (let j = i; j < Math.min(i + 4, lines.length); j++) {
        if (/wrote:\s*$/i.test(lines[j].trim())) {
          return {
            main: lines.slice(0, i).join('\n').trimEnd() || null,
            quoted: lines.slice(i).join('\n').trim(),
          };
        }
      }
    }

    // First ">" quoted line (after at least one non-empty new-content line)
    if (trimmed.startsWith('>') && i > 0) {
      return {
        main: lines.slice(0, i).join('\n').trimEnd() || null,
        quoted: lines.slice(i).join('\n').trim(),
      };
    }
  }

  return { main: text, quoted: null };
}

// Gmail-style message body: shows new content; quoted tail hidden behind a ··· button.
function MessageBody({ text }) {
  const [open, setOpen] = useState(false);
  const { main, quoted } = splitQuotedText(text);
  return (
    <div className="msg-body">
      {main && <span style={{ whiteSpace: 'pre-wrap' }}>{main}</span>}
      {quoted && (
        <>
          <button
            className="msg-quote-toggle"
            onClick={() => setOpen(o => !o)}
            title={open ? 'Hide quoted text' : 'Show quoted text'}
          >···</button>
          {open && <div className="msg-quote-body">{quoted}</div>}
        </>
      )}
      {!main && !quoted && null}
    </div>
  );
}

// Scroll a message container so its LAST (newest) message sits at the top of the
// pane — you land at the start of the newest message, not its tail end.
const scrollLastToTop = (container) => {
  if (!container) return;
  const last = container.lastElementChild;
  if (last) container.scrollTop += last.getBoundingClientRect().top - container.getBoundingClientRect().top;
};

const PaperclipIcon = ({ size = 12 }) => <Ico size={size} d="M21.44 11.05l-9.19 9.19a5 5 0 01-7.07-7.07l9.19-9.19a3 3 0 014.24 4.24l-9.2 9.19a1 1 0 01-1.41-1.41l8.49-8.49" />;

// Fetch an attachment (with auth) and open it in a new tab.
async function openAttachment(attId) {
  try {
    const res = await api.get(`/hosts/attachments/${attId}`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { /* ignore */ }
}

const fmtBytes = (n) => n >= 1048576 ? (n / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round((n || 0) / 1024)) + ' KB';

// Download chips shown under a message that has attachments.
function AttachmentChips({ items }) {
  if (!items || !items.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
      {items.map(a => (
        <button key={a.id} type="button" onClick={() => openAttachment(a.id)} title={`Open ${a.filename}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', border: '1px solid var(--line)', borderRadius: 6, background: 'var(--bg-elev)', fontSize: 11.5, color: 'var(--ink-2)', cursor: 'pointer' }}>
          <PaperclipIcon /> {a.filename} <span style={{ color: 'var(--ink-3)' }}>· {fmtBytes(a.size_bytes)}</span>
        </button>
      ))}
    </div>
  );
}

// A compact file picker + selected-file list for compose/reply forms.
function AttachmentPicker({ files, setFiles }) {
  const onPick = (e) => { setFiles([...(files || []), ...Array.from(e.target.files || [])]); e.target.value = ''; };
  const remove = (i) => setFiles(files.filter((_, idx) => idx !== i));
  return (
    <div style={{ margin: '2px 0 6px' }}>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--ink-2)', cursor: 'pointer' }}>
        <PaperclipIcon /> Attach files
        <input type="file" multiple onChange={onPick} style={{ display: 'none' }} />
      </label>
      {(files || []).map((f, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 8, padding: '2px 6px', background: 'var(--line-2)', borderRadius: 6, fontSize: 11.5 }}>
          {f.name} <span style={{ color: 'var(--ink-3)' }}>· {fmtBytes(f.size)}</span>
          <button type="button" onClick={() => remove(i)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ink-3)', fontSize: 13, lineHeight: 1 }}>×</button>
        </span>
      ))}
    </div>
  );
}
const initials = (f, l) => `${(f || '?')[0] || ''}${(l || '')[0] || ''}`.toUpperCase();
const fmtWhen = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const now = new Date();
  const sameDay = dt.toDateString() === now.toDateString();
  return sameDay
    ? dt.toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' })
    : dt.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
};

export default function InboxPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [openKey, setOpenKey] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [hostTemplates, setHostTemplates] = useState([]);
  const [selectedHost, setSelectedHost] = useState(null);
  const [section, setSection] = useState('students'); // primary split: 'students' | 'hosts'
  const [tab, setTab] = useState('inbox');   // student sub-tab: 'inbox' | 'sent'
  const [hostView, setHostView] = useState('shared'); // host sub-view: 'shared' | 'unmatched' | 'search'
  const [sharedThreads, setSharedThreads] = useState([]);
  const [selectedShared, setSelectedShared] = useState(null);
  const [unmatched, setUnmatched] = useState([]);
  const [selectedUnmatched, setSelectedUnmatched] = useState(null);

  useEffect(() => {
    api.get('/inbox/templates').then(r => setTemplates(r.data)).catch(() => {});
    api.get('/hosts/message-templates').then(r => setHostTemplates(r.data)).catch(() => {});
  }, []);

  const hasLoaded = useRef(false);
  const fetchThreads = useCallback(async () => {
    if (section === 'hosts') { setLoading(false); return; }   // host section manages its own data
    try {
      const res = await api.get(tab === 'sent' ? '/inbox/sent' : '/inbox');
      setThreads(res.data);
      setError('');
      hasLoaded.current = true;
    } catch {
      if (!hasLoaded.current) setError(`Failed to load ${tab}`); // stay quiet on background poll blips
    } finally { setLoading(false); }
  }, [tab, section]);

  // Switching the student sub-tab (Inbox/Sent): reset the list + close any open thread.
  const switchTab = (next) => {
    if (next === tab) return;
    setTab(next);
    setThreads([]);
    setOpenKey(null);
    hasLoaded.current = false;
  };

  // Switching the primary section (Student Emails / Host Emails).
  const switchSection = (next) => {
    if (next === section) return;
    setSection(next);
    setOpenKey(null);
    setSelectedHost(null);
    setSelectedShared(null);
    setSelectedUnmatched(null);
    setHostView('shared');
    setThreads([]);
    setSearch('');
    hasLoaded.current = false;
  };

  // Live: poll the shared + unmatched host inboxes so cold/forwarded emails appear
  // for every coordinator without a manual refresh.
  const fetchShared = useCallback(async () => {
    if (section !== 'hosts') return;
    try {
      const [s, u] = await Promise.all([
        api.get('/hosts/shared-inbox'),
        api.get('/hosts/unmatched-inbox'),
      ]);
      setSharedThreads(s.data);
      setUnmatched(u.data);
    } catch { /* ignore */ }
  }, [section]);
  useEffect(() => {
    fetchShared();
    const iv = setInterval(fetchShared, 15000);
    const onEvt = () => fetchShared();
    window.addEventListener('focus', onEvt);
    return () => { clearInterval(iv); window.removeEventListener('focus', onEvt); };
  }, [fetchShared]);

  const [inboxUnread, setInboxUnread] = useState(0);   // student unread
  const [sharedUnread, setSharedUnread] = useState(0); // shared host unread (per-user)
  const [unmatchedUnread, setUnmatchedUnread] = useState(0); // unmatched unread (per-user)
  const refreshUnread = useCallback(async () => {
    try {
      const r = await api.get('/inbox/unread-count');
      setInboxUnread(r.data.students ?? r.data.count ?? 0);
      setSharedUnread(r.data.shared ?? 0);
      setUnmatchedUnread(r.data.unmatched ?? 0);
    } catch { /* ignore */ }
  }, []);

  // Live: refresh the list + unread count on an interval, on window focus, and
  // whenever a thread is read elsewhere — so new mail appears without a refresh.
  useEffect(() => {
    fetchThreads();
    refreshUnread();
    const iv = setInterval(() => { fetchThreads(); refreshUnread(); }, 20000);
    const onEvt = () => { fetchThreads(); refreshUnread(); };
    window.addEventListener('focus', onEvt);
    window.addEventListener('inbox-updated', onEvt);
    return () => {
      clearInterval(iv);
      window.removeEventListener('focus', onEvt);
      window.removeEventListener('inbox-updated', onEvt);
    };
  }, [fetchThreads, refreshUnread]);

  const filtered = useMemo(() => threads.filter(t => {
    if (!search) return true;
    const hay = [t.first_name, t.last_name, t.email, t.subject, t.program_code].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(search.toLowerCase());
  }), [threads, search]);

  // Locally zero-out a thread's unread badge after it's opened + marked read
  const markThreadRead = useCallback((threadKey) => {
    setThreads(prev => prev.map(t => t.thread_key === threadKey ? { ...t, unread_count: 0 } : t));
    window.dispatchEvent(new Event('inbox-updated'));
  }, []);

  // Auto-collapse expand view when no thread is selected
  useEffect(() => {
    if (!openKey && !selectedHost) setExpanded(false);
  }, [openKey, selectedHost]);

  if (loading) return <PageLoad />;

  const openThread = threads.find(t => t.thread_key === openKey);

  return (
    <div className="inbox-wrap">
      {/* Header */}
      <div className="inbox-header">
        <div className="inbox-header-title">
          <div className="inbox-header-icon"><MailIcon size={20} /></div>
          <div>
            <h1>Email Inbox</h1>
            <p>{section === 'hosts'
              ? 'Email your host partners — each program keeps its own separate conversation.'
              : 'Replies from your students land here — read and respond without leaving the app.'}</p>
          </div>
        </div>
        {section === 'students' && inboxUnread > 0 && <span className="inbox-header-badge">{inboxUnread} unread</span>}
      </div>

      {/* Primary split: Student Emails vs Host Emails */}
      <div className="inbox-sections">
        <button className={section === 'students' ? 'active' : ''} onClick={() => switchSection('students')}>
          <UserIcon size={14} /> Student Emails
          {inboxUnread > 0 && <span className="inbox-tab-count">{inboxUnread}</span>}
        </button>
        <button className={section === 'hosts' ? 'active' : ''} onClick={() => switchSection('hosts')}>
          <MailIcon size={14} /> Host Emails
          {sharedUnread > 0 && <span className="inbox-tab-count">{sharedUnread}</span>}
        </button>
      </div>

      {error && <ErrorMsg>{error}</ErrorMsg>}

      <div className={`inbox-panes${(openKey || selectedHost) ? ' has-open' : ''}${expanded ? ' expanded' : ''}`}>
        {/* ── List pane ── */}
        <div className="inbox-list">
          {section === 'hosts' ? (
            <>
              <div className="inbox-tabs">
                <button className={hostView === 'shared' ? 'active' : ''}
                  onClick={() => { setHostView('shared'); setSelectedHost(null); setSelectedUnmatched(null); }}>
                  <ReplyDown size={13} /> Shared
                  {sharedUnread > 0 && <span className="inbox-tab-count">{sharedUnread}</span>}
                </button>
                <button className={hostView === 'unmatched' ? 'active' : ''}
                  onClick={() => { setHostView('unmatched'); setSelectedHost(null); setSelectedShared(null); }}>
                  <MailIcon size={13} /> Unmatched
                  {unmatchedUnread > 0 && <span className="inbox-tab-count">{unmatchedUnread}</span>}
                </button>
                <button className={hostView === 'search' ? 'active' : ''}
                  title="Find a host"
                  onClick={() => { setHostView('search'); setSelectedShared(null); setSelectedUnmatched(null); }}>
                  <SearchIcon /> <span className="tab-label-hide-narrow">Find a host</span>
                </button>
              </div>
              {hostView === 'shared' ? (
                <SharedInboxList
                  threads={sharedThreads}
                  selectedKey={selectedShared?.thread_key}
                  onSelect={(t) => { setSelectedShared(t); setSelectedHost(null); setSelectedUnmatched(null); }}
                />
              ) : hostView === 'unmatched' ? (
                <UnmatchedList
                  items={unmatched}
                  selectedId={selectedUnmatched?.id}
                  onSelect={(m) => { setSelectedUnmatched(m); setSelectedHost(null); setSelectedShared(null); }}
                />
              ) : (
                <HostSearchList
                  user={user}
                  selectedId={selectedHost?.id}
                  onSelect={(h) => { setSelectedHost(h); setSelectedShared(null); setSelectedUnmatched(null); }}
                />
              )}
            </>
          ) : (
          <>
          <div className="inbox-tabs">
            <button className={tab === 'inbox' ? 'active' : ''} onClick={() => switchTab('inbox')}>
              <ReplyDown size={13} /> Inbox
              {inboxUnread > 0 && <span className="inbox-tab-count">{inboxUnread}</span>}
            </button>
            <button className={tab === 'sent' ? 'active' : ''} onClick={() => switchTab('sent')}>
              <SendIcon size={12} /> Sent
            </button>
          </div>

          <div className="inbox-search">
            <SearchIcon />
            <input placeholder={tab === 'sent' ? 'Search sent…' : 'Search inbox…'} value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          {filtered.length === 0 ? (
            <div className="inbox-empty">
              <MailIcon size={28} />
              <p>{search
                ? 'No conversations match your search.'
                : tab === 'sent' ? 'Nothing sent yet.' : 'No student emails yet.'}</p>
              {!search && <span>{tab === 'sent'
                ? 'Emails you send to your students will appear here.'
                : 'When a student replies to one of your emails, it will appear here.'}</span>}
            </div>
          ) : (
            <ul className="inbox-threads">
              {filtered.map(t => {
                const unread = t.unread_count > 0;
                return (
                  <li
                    key={t.thread_key}
                    className={`inbox-thread${unread ? ' unread' : ''}${openKey === t.thread_key ? ' active' : ''}`}
                    onClick={() => setOpenKey(t.thread_key)}
                  >
                    <div className="inbox-avatar">{initials(t.first_name, t.last_name)}</div>
                    <div className="inbox-thread-main">
                      <div className="inbox-thread-top">
                        <span className="inbox-thread-subject-line">{t.subject}</span>
                        <span className="inbox-thread-when">{fmtWhen(t.last_at)}</span>
                      </div>
                      <div className="inbox-thread-from">
                        {tab === 'sent'
                          ? <><SendIcon size={10} /> To: {t.first_name} {t.last_name}</>
                          : <><ReplyDown size={11} /> {t.first_name} {t.last_name}</>}
                      </div>
                      <div className="inbox-thread-snippet">{t.last_snippet || 'No preview available'}</div>
                      <div className="inbox-thread-tags">
                        {t.program_code && <span className="inbox-tag">{t.program_code}</span>}
                        {t.message_count > 1 && <span className="inbox-tag muted">{t.message_count} messages</span>}
                      </div>
                    </div>
                    {unread && <span className="inbox-unread-dot" title={`${t.unread_count} unread`} />}
                  </li>
                );
              })}
            </ul>
          )}
          </>
          )}
        </div>

        {/* ── Reading pane ── */}
        <div className="inbox-reader">
          {section === 'hosts' ? (
            selectedUnmatched ? (
              <UnmatchedReader
                key={selectedUnmatched.id}
                email={selectedUnmatched}
                onBack={() => setSelectedUnmatched(null)}
                onResolved={() => { setSelectedUnmatched(null); fetchShared(); refreshUnread(); }}
                expanded={expanded} onExpand={() => setExpanded(e => !e)}
              />
            ) : selectedShared ? (
              <SharedThreadReader
                key={selectedShared.thread_key}
                thread={selectedShared}
                user={user}
                onBack={() => setSelectedShared(null)}
                onSent={fetchShared}
                navigate={navigate}
                expanded={expanded} onExpand={() => setExpanded(e => !e)}
              />
            ) : selectedHost ? (
              <HostComposeReader
                key={selectedHost.id}
                host={selectedHost}
                user={user}
                templates={hostTemplates}
                onBack={() => setSelectedHost(null)}
                navigate={navigate}
                expanded={expanded} onExpand={() => setExpanded(e => !e)}
              />
            ) : (
              <div className="inbox-reader-empty">
                <MailIcon size={36} />
                <p>{hostView === 'shared'
                  ? 'Pick a shared host email to read and reply — everyone on that host sees it live.'
                  : hostView === 'unmatched'
                  ? 'Forwarded emails we couldn’t match to a host land here. Open one and assign it to the right host.'
                  : 'Search a host by name to start an email.'}</p>
              </div>
            )
          ) : openThread ? (
            <ThreadView
              key={openThread.thread_key}
              thread={openThread}
              templates={templates}
              onBack={() => setOpenKey(null)}
              onRead={() => markThreadRead(openThread.thread_key)}
              navigate={navigate}
              expanded={expanded} onExpand={() => setExpanded(e => !e)}
            />
          ) : (
            <div className="inbox-reader-empty">
              <MailIcon size={36} />
              <p>Select a conversation to read and reply.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Conversation view + inline reply ─────────────────── */
function ThreadView({ thread, templates = [], onBack, onRead, navigate, expanded, onExpand }) {
  const sid = thread.student_id;
  const threadSubject = thread.base_subject || '';
  const [messages, setMessages] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [inserting, setInserting] = useState(false);
  const [lastTpl, setLastTpl] = useState('');   // last template text inserted (so a new pick replaces it)
  const [err, setErr] = useState('');
  const [files, setFiles] = useState([]);
  const bodyRef = useRef(null);
  useEffect(() => { scrollLastToTop(bodyRef.current); }, [messages?.length]);

  // Insert a template's rendered-for-this-student body. Picking another template
  // swaps out the previously inserted one instead of stacking, but keeps any text
  // the PC typed themselves.
  const applyTemplate = async (triggerType) => {
    setInserting(true); setErr('');
    try {
      const res = await api.get(`/students/${sid}/email-preview/${triggerType}`);
      const body = (res.data.body || '').trim();
      setReply(prev => {
        let base = prev;
        if (lastTpl && base.includes(lastTpl)) {
          base = base.replace(lastTpl, '').replace(/\n{3,}/g, '\n\n').trim();
        }
        return base ? `${base.trimEnd()}\n\n${body}` : body;
      });
      setLastTpl(body);
    } catch {
      setErr('Could not load that template.');
    } finally { setInserting(false); }
  };

  // Only the messages belonging to THIS subject thread
  const load = useCallback(async () => {
    try {
      const res = await api.get(`/students/${sid}/email-log`);
      setMessages(res.data
        .filter(m => baseSubject(m.subject) === threadSubject)
        .sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at)));
    } catch { setErr('Failed to load conversation'); }
  }, [sid, threadSubject]);

  // Live: poll the open thread so incoming replies appear without a refresh, and
  // keep it marked read while it's on screen.
  useEffect(() => {
    const tick = () => {
      load();
      api.post(`/inbox/students/${sid}/read`, { subject: threadSubject }).then(onRead).catch(() => {});
    };
    tick();
    const iv = setInterval(tick, 15000);
    return () => clearInterval(iv);
  }, [thread.thread_key]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('subject', 'Re: ' + (threadSubject || 'your practicum'));
      fd.append('body', reply);
      files.forEach(f => fd.append('attachments', f));
      await api.post(`/students/${sid}/send-direct`, fd);
      setReply('');
      setLastTpl('');
      setFiles([]);
      await load();
    } catch (e2) {
      setErr(e2.response?.data?.error || 'Failed to send reply');
    } finally { setSending(false); }
  };

  return (
    <div className="thread">
      <div className="thread-head">
        <button className="thread-back" onClick={onBack} title="Back"><BackIcon /></button>
        <div className="inbox-avatar sm">{initials(thread.first_name, thread.last_name)}</div>
        <div className="thread-head-main">
          <div className="thread-head-name">{thread.first_name} {thread.last_name}</div>
          <div className="thread-head-sub">{thread.email}{thread.host_org_name ? ` · ${thread.host_org_name}` : ''}</div>
        </div>
        <button className="thread-profile" onClick={() => navigate(`/students/${sid}`)}>
          <UserIcon /> Profile
        </button>
        <button className="thread-expand" onClick={onExpand} title={expanded ? 'Collapse' : 'Full view'}>
          {expanded ? <CollapseIcon /> : <ExpandIcon />}
          {expanded ? 'Collapse' : 'Full view'}
        </button>
      </div>

      <div className="thread-body" ref={bodyRef}>
        {messages === null ? (
          <div className="thread-loading">Loading conversation…</div>
        ) : messages.length === 0 ? (
          <div className="thread-loading">No messages.</div>
        ) : (
          messages.map((m, i) => {
            const isOut = m.direction !== 'inbound';
            return (
              <div key={i} className={`msg ${isOut ? 'out' : 'in'}`}>
                <div className="msg-head">
                  <span className="msg-icon">{isOut ? <SendIcon size={10} /> : <ReplyDown size={11} />}</span>
                  <span className="msg-from">{isOut ? 'MCG Career Services' : (m.metadata?.fromName || m.from_address || 'Student')}</span>
                  {isOut && m.recipient && <span className="msg-to">→ {m.recipient}</span>}
                  <span className="msg-when">{new Date(m.sent_at).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                </div>
                {m.subject && <div className="msg-subject">{m.subject}</div>}
                {m.metadata?.body_text
                  ? <MessageBody text={m.metadata.body_text} />
                  : <div className="msg-nobody">No body preview available.</div>}
                <AttachmentChips items={m.attachments} />
              </div>
            );
          })
        )}
      </div>

      <form className="thread-reply" onSubmit={sendReply}>
        <div className="thread-reply-head">
          <div className="thread-reply-label">Reply to {thread.first_name} · <span>Re: {threadSubject || 'your practicum'}</span></div>
          {templates.length > 0 && (
            <TemplatePicker templates={templates} onPick={applyTemplate} loading={inserting} />
          )}
        </div>
        <textarea
          placeholder="Write your reply…"
          value={reply}
          onChange={e => setReply(e.target.value)}
          onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') sendReply(e); }}
        />
        <AttachmentPicker files={files} setFiles={setFiles} />
        {err && <div className="thread-reply-err">{err}</div>}
        <div className="thread-reply-actions">
          <button type="submit" className="thread-send" disabled={sending || !reply.trim()}>
            <SendIcon /> {sending ? 'Sending…' : 'Send reply'}
          </button>
          <span className="thread-reply-hint">⌘/Ctrl + Enter to send</span>
        </div>
      </form>
    </div>
  );
}

/* ── Template picker (insert a rendered template into the reply) ── */
const TemplateIcon = ({ size = 13 }) => <Ico size={size} d={<g><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M8 4v16" /></g>} />;
const ChevronIcon = ({ size = 13 }) => <Ico size={size} d="M6 9l6 6 6-6" />;

function TemplatePicker({ templates, onPick, loading }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div className="tpl-picker" ref={ref}>
      <button type="button" className="tpl-btn" onClick={() => setOpen(o => !o)} disabled={loading}>
        <TemplateIcon /> {loading ? 'Inserting…' : 'Use template'} <ChevronIcon size={12} />
      </button>
      {open && (
        <div className="tpl-menu">
          <div className="tpl-menu-head">Insert a template</div>
          {templates.map(t => (
            <button
              key={t.trigger_type}
              type="button"
              className="tpl-item"
              onClick={() => { onPick(t.trigger_type); setOpen(false); }}
              title={t.trigger_desc || ''}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Hosts tab: host search (left pane) ───────────────── */
function HostSearchList({ user, selectedId, onSelect }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (!term) { setResults([]); return; }
    let alive = true;
    setLoading(true);
    const h = setTimeout(async () => {
      try {
        const r = await api.get(`/hosts/search`, { params: { q: term } });
        if (alive) setResults(r.data);
      } catch { if (alive) setResults([]); }
      finally { if (alive) setLoading(false); }
    }, 220);
    return () => { alive = false; clearTimeout(h); };
  }, [q]);

  return (
    <>
      <div className="inbox-search">
        <SearchIcon />
        <input placeholder="Type a host name…" value={q} onChange={e => setQ(e.target.value)} autoFocus />
      </div>
      {q.trim() === '' ? (
        <div className="inbox-empty">
          <MailIcon size={28} />
          <p>Find a host</p>
          <span>Start typing an organization name — you’ll only see hosts set up for your program.</span>
        </div>
      ) : loading && results.length === 0 ? (
        <div className="inbox-empty"><p>Searching…</p></div>
      ) : results.length === 0 ? (
        <div className="inbox-empty"><p>No matching hosts.</p></div>
      ) : (
        <ul className="inbox-threads">
          {results.map(h => {
            const contact = [h.contact_first_name, h.contact_last_name].filter(Boolean).join(' ');
            return (
              <li
                key={h.id}
                className={`inbox-thread${selectedId === h.id ? ' active' : ''}`}
                onClick={() => onSelect(h)}
              >
                <div className="inbox-avatar">{(h.org_name || '?').slice(0, 2).toUpperCase()}</div>
                <div className="inbox-thread-main">
                  <div className="inbox-thread-top">
                    <span className="inbox-thread-subject-line">{h.org_name}</span>
                  </div>
                  <div className="inbox-thread-snippet">{contact || h.contact_email}{h.city ? ` · ${h.city}` : ''}</div>
                  <div className="inbox-thread-tags">
                    {(h.programs_accepted || []).map(p => <span key={p} className="inbox-tag">{p}</span>)}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

/* ── Hosts tab: compose + conversation (right pane) ───── */
function HostComposeReader({ host, user, templates = [], onBack, navigate, expanded, onExpand }) {
  const isPriv = user.role === 'admin' || !!user.is_host_specialist;
  const hostProgs = host.programs_accepted || [];
  const sendable = isPriv ? hostProgs : (user.program_codes || []).filter(c => hostProgs.includes(c));

  const [program, setProgram] = useState(sendable[0] || '');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [inserting, setInserting] = useState(false);
  const [err, setErr] = useState('');
  const [log, setLog] = useState(null);
  const [files, setFiles] = useState([]);
  const bodyRef = useRef(null);
  useEffect(() => { scrollLastToTop(bodyRef.current); }, [log?.length]);

  const contactName = [host.contact_first_name, host.contact_last_name].filter(Boolean).join(' ') || host.org_name;

  const load = useCallback(async () => {
    try { const r = await api.get(`/hosts/${host.id}/email-log`); setLog(r.data); }
    catch { setLog([]); }
  }, [host.id]);
  useEffect(() => { load(); }, [load]);

  const thread = (log || [])
    .filter(e => e.direction === 'inbound' || e.trigger_type === 'direct')
    .sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));

  const applyTemplate = async (trigger) => {
    setInserting(true); setErr('');
    try {
      const r = await api.get(`/hosts/${host.id}/message-preview`, { params: { trigger, program_code: program || undefined } });
      setSubject(r.data.subject || '');
      setBody(r.data.body || '');
    } catch { setErr('Could not load that template.'); }
    finally { setInserting(false); }
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    setSending(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('subject', subject.trim());
      fd.append('body', body.trim());
      if (program) fd.append('program_code', program);
      files.forEach(f => fd.append('attachments', f));
      await api.post(`/hosts/${host.id}/message`, fd);
      setSubject(''); setBody(''); setFiles([]);
      await load();
    } catch (e2) {
      setErr(e2.response?.data?.error || 'Failed to send');
    } finally { setSending(false); }
  };

  return (
    <div className="thread">
      <div className="thread-head">
        <button className="thread-back" onClick={onBack} title="Back"><BackIcon /></button>
        <div className="inbox-avatar sm">{(host.org_name || '?').slice(0, 2).toUpperCase()}</div>
        <div className="thread-head-main">
          <div className="thread-head-name">{host.org_name}</div>
          <div className="thread-head-sub">{contactName} · {host.contact_email}</div>
        </div>
        <button className="thread-profile" onClick={() => navigate(`/hosts/${host.id}`)}>
          <UserIcon /> Host
        </button>
        <button className="thread-expand" onClick={onExpand} title={expanded ? 'Collapse' : 'Full view'}>
          {expanded ? <CollapseIcon /> : <ExpandIcon />}
          {expanded ? 'Collapse' : 'Full view'}
        </button>
      </div>

      <div className="thread-body" ref={bodyRef}>
        {log === null ? (
          <div className="thread-loading">Loading conversation…</div>
        ) : thread.length === 0 ? (
          <div className="thread-loading">No messages yet — send the first one below.</div>
        ) : (
          thread.map((m, i) => {
            const isOut = m.direction !== 'inbound';
            return (
              <div key={i} className={`msg ${isOut ? 'out' : 'in'}`}>
                <div className="msg-head">
                  <span className="msg-icon">{isOut ? <SendIcon size={10} /> : <ReplyDown size={11} />}</span>
                  <span className="msg-from">{isOut ? 'MCG Career Services' : (m.metadata?.fromName || m.from_address || 'Host')}</span>
                  {m.program_code && <span className="inbox-tag">{m.program_code}</span>}
                  <span className="msg-when">{new Date(m.sent_at).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                </div>
                {m.subject && <div className="msg-subject">{m.subject}</div>}
                {m.metadata?.body_text
                  ? <MessageBody text={m.metadata.body_text} />
                  : <div className="msg-nobody">No body preview available.</div>}
                <AttachmentChips items={m.attachments} />
              </div>
            );
          })
        )}
      </div>

      <form className="thread-reply" onSubmit={send}>
        <div className="thread-reply-head">
          <div className="thread-reply-label">
            Email {host.org_name}
            {sendable.length > 1 ? (
              <>  · <select value={program} onChange={e => setProgram(e.target.value)}
                    style={{ fontSize: 12, padding: '2px 6px', borderRadius: 6 }}>
                    {sendable.map(c => <option key={c} value={c}>{c}</option>)}
                  </select></>
            ) : (
              <> · as <span className="inbox-tag">{program}</span></>
            )}
          </div>
          {templates.length > 0 && (
            <TemplatePicker templates={templates} onPick={applyTemplate} loading={inserting} />
          )}
        </div>
        {sendable.length === 0 ? (
          <div className="thread-reply-err">This host isn’t set up for your program.</div>
        ) : (
          <>
            <input
              className="host-compose-subject"
              placeholder="Subject…"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 8, fontSize: 13, marginBottom: 8 }}
            />
            <textarea
              placeholder={`Write to ${host.contact_email}…`}
              value={body}
              onChange={e => setBody(e.target.value)}
              onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') send(e); }}
            />
            <AttachmentPicker files={files} setFiles={setFiles} />
            {err && <div className="thread-reply-err">{err}</div>}
            <div className="thread-reply-actions">
              <button type="submit" className="thread-send" disabled={sending || !subject.trim() || !body.trim()}>
                <SendIcon /> {sending ? 'Sending…' : 'Send email'}
              </button>
              <span className="thread-reply-hint">The host’s reply returns to this program only</span>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

/* ── Shared host inbox: list of cold-email threads from multi-program hosts ──── */
function SharedInboxList({ threads, selectedKey, onSelect }) {
  if (!threads.length) {
    return (
      <div className="inbox-empty">
        <MailIcon size={28} />
        <p>No shared host emails</p>
        <span>When a host that works with more than one program emails in, it lands here for all of that host’s coordinators to see and reply to together.</span>
      </div>
    );
  }
  return (
    <ul className="inbox-threads">
      {threads.map(t => (
        <li
          key={t.thread_key}
          className={`inbox-thread${selectedKey === t.thread_key ? ' active' : ''}`}
          onClick={() => onSelect(t)}
        >
          <div className="inbox-avatar">{(t.org_name || '?').slice(0, 2).toUpperCase()}</div>
          <div className="inbox-thread-main">
            <div className="inbox-thread-top">
              <span className="inbox-thread-subject-line">{t.org_name}</span>
              <span className="inbox-thread-when">{fmtWhen(t.last_at)}</span>
            </div>
            <div className="inbox-thread-from"><ReplyDown size={11} /> {t.subject}</div>
            <div className="inbox-thread-snippet">{t.last_snippet || 'No preview available'}</div>
            <div className="inbox-thread-tags">
              {(t.programs || []).map(p => <span key={p} className="inbox-tag">{p}</span>)}
              {t.message_count > 1 && <span className="inbox-tag muted">{t.message_count} messages</span>}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ── Shared thread: conversation + shared reply (stays shared for all PCs) ───── */
function SharedThreadReader({ thread, user, onBack, onSent, navigate, expanded, onExpand }) {
  const hostId = thread.host_id;
  const threadSubject = thread.subject || '';
  const [messages, setMessages] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const [files, setFiles] = useState([]);
  const bodyRef = useRef(null);
  // Land at the START of the newest message (pin it to the top of the pane).
  useEffect(() => { scrollLastToTop(bodyRef.current); }, [messages?.length]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/hosts/${hostId}/email-log`);
      // Shared thread = program_code NULL messages for this host + subject,
      // ordered oldest → newest (Gmail-style: newest sits by the reply box).
      setMessages(r.data.filter(m =>
        m.program_code == null &&
        (m.direction === 'inbound' || m.trigger_type === 'direct') &&
        baseSubject(m.subject) === threadSubject
      ).sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at)));
      // Mark this thread read for THIS coordinator only, then refresh the badges.
      api.post(`/hosts/${hostId}/shared-read`, { subject: threadSubject })
        .then(() => window.dispatchEvent(new Event('inbox-updated')))
        .catch(() => {});
    } catch { setErr('Failed to load conversation'); }
  }, [hostId, threadSubject]);

  // Live: poll so a reply from another coordinator shows up without a refresh.
  useEffect(() => {
    load();
    const iv = setInterval(load, 12000);
    return () => clearInterval(iv);
  }, [load]);

  const send = async (e) => {
    e?.preventDefault();
    if (!reply.trim()) return;
    setSending(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('subject', 'Re: ' + (threadSubject || 'your message'));
      fd.append('body', reply);
      files.forEach(f => fd.append('attachments', f));
      await api.post(`/hosts/${hostId}/shared-reply`, fd);
      setReply('');
      setFiles([]);
      await load();
      onSent?.();
    } catch (e2) {
      setErr(e2.response?.data?.error || 'Failed to send');
    } finally { setSending(false); }
  };

  return (
    <div className="thread">
      <div className="thread-head">
        <button className="thread-back" onClick={onBack} title="Back"><BackIcon /></button>
        <div className="inbox-avatar sm">{(thread.org_name || '?').slice(0, 2).toUpperCase()}</div>
        <div className="thread-head-main">
          <div className="thread-head-name">{thread.org_name} <span className="inbox-tag muted" style={{ marginLeft: 6 }}>Shared</span></div>
          <div className="thread-head-sub">{threadSubject} · {(thread.programs || []).join(' + ')}</div>
        </div>
        <button className="thread-profile" onClick={() => navigate(`/hosts/${hostId}`)}>
          <UserIcon /> Host
        </button>
        <button className="thread-expand" onClick={onExpand} title={expanded ? 'Collapse' : 'Full view'}>
          {expanded ? <CollapseIcon /> : <ExpandIcon />}
          {expanded ? 'Collapse' : 'Full view'}
        </button>
      </div>

      <div className="thread-body" ref={bodyRef}>
        {messages === null ? (
          <div className="thread-loading">Loading conversation…</div>
        ) : messages.length === 0 ? (
          <div className="thread-loading">No messages.</div>
        ) : (
          messages.map((m, i) => {
            const isOut = m.direction !== 'inbound';
            return (
              <div key={i} className={`msg ${isOut ? 'out' : 'in'}`}>
                <div className="msg-head">
                  <span className="msg-icon">{isOut ? <SendIcon size={10} /> : <ReplyDown size={11} />}</span>
                  <span className="msg-from">{isOut ? (m.metadata?.author || 'MCG Career Services') : (m.metadata?.fromName || m.from_address || 'Host')}</span>
                  <span className="msg-when">{new Date(m.sent_at).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                </div>
                {m.subject && <div className="msg-subject">{m.subject}</div>}
                {m.metadata?.body_text
                  ? <MessageBody text={m.metadata.body_text} />
                  : <div className="msg-nobody">No body preview available.</div>}
                <AttachmentChips items={m.attachments} />
              </div>
            );
          })
        )}
      </div>

      <form className="thread-reply" onSubmit={send}>
        <div className="thread-reply-head">
          <div className="thread-reply-label">Reply · <span>this stays shared with every coordinator on {thread.org_name}</span></div>
        </div>
        <textarea
          placeholder="Write a reply…"
          value={reply}
          onChange={e => setReply(e.target.value)}
          onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') send(e); }}
        />
        <AttachmentPicker files={files} setFiles={setFiles} />
        {err && <div className="thread-reply-err">{err}</div>}
        <div className="thread-reply-actions">
          <button type="submit" className="thread-send" disabled={sending || !reply.trim()}>
            <SendIcon /> {sending ? 'Sending…' : 'Send reply'}
          </button>
          <span className="thread-reply-hint">⌘/Ctrl + Enter to send</span>
        </div>
      </form>
    </div>
  );
}

/* ── Unmatched: forwarded emails we couldn't tie to a host (list) ────────────── */
function UnmatchedList({ items, selectedId, onSelect }) {
  if (!items.length) {
    return (
      <div className="inbox-empty">
        <MailIcon size={28} />
        <p>Nothing to triage</p>
        <span>When a coordinator forwards a department email (e.g. business@mcgcollege.com) here and we can’t match the host automatically, it lands here for anyone to assign.</span>
      </div>
    );
  }
  return (
    <ul className="inbox-threads">
      {items.map(m => (
        <li
          key={m.id}
          className={`inbox-thread${m.unread ? ' unread' : ''}${selectedId === m.id ? ' active' : ''}`}
          onClick={() => onSelect(m)}
        >
          <div className="inbox-avatar">{(m.metadata?.fromName || m.from_address || '?').slice(0, 2).toUpperCase()}</div>
          <div className="inbox-thread-main">
            <div className="inbox-thread-top">
              <span className="inbox-thread-subject-line">{m.subject || '(no subject)'}</span>
              <span className="inbox-thread-when">{fmtWhen(m.sent_at)}</span>
            </div>
            <div className="inbox-thread-from"><MailIcon size={11} /> Forwarded by {m.metadata?.fromName || m.from_address}</div>
            <div className="inbox-thread-snippet">{m.metadata?.body_text || 'No preview available'}</div>
          </div>
          {m.unread && <span className="inbox-unread-dot" title="Unread" />}
        </li>
      ))}
    </ul>
  );
}

/* ── Unmatched: read the email + assign it to the right host ─────────────────── */
function UnmatchedReader({ email, onBack, onResolved, expanded, onExpand }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [err, setErr] = useState('');

  // Mark read for this user on open (keeps everyone else's badge intact).
  useEffect(() => {
    api.post(`/hosts/unmatched/${email.id}/read`)
      .then(() => window.dispatchEvent(new Event('inbox-updated')))
      .catch(() => {});
  }, [email.id]);

  // Host search for the assignment picker.
  useEffect(() => {
    const term = q.trim();
    if (!term) { setResults([]); return; }
    let alive = true;
    const h = setTimeout(async () => {
      try { const r = await api.get('/hosts/search', { params: { q: term } }); if (alive) setResults(r.data); }
      catch { if (alive) setResults([]); }
    }, 220);
    return () => { alive = false; clearTimeout(h); };
  }, [q]);

  const assign = async (hostId) => {
    setAssigning(true); setErr('');
    try {
      await api.post(`/hosts/unmatched/${email.id}/assign`, { host_id: hostId });
      onResolved?.();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to assign');
    } finally { setAssigning(false); }
  };

  return (
    <div className="thread">
      <div className="thread-head">
        <button className="thread-back" onClick={onBack} title="Back"><BackIcon /></button>
        <div className="inbox-avatar sm">{(email.metadata?.fromName || email.from_address || '?').slice(0, 2).toUpperCase()}</div>
        <div className="thread-head-main">
          <div className="thread-head-name">{email.subject || '(no subject)'} <span className="inbox-tag muted" style={{ marginLeft: 6 }}>Unmatched</span></div>
          <div className="thread-head-sub">Forwarded by {email.metadata?.fromName || email.from_address}</div>
        </div>
        <button className="thread-expand" onClick={onExpand} title={expanded ? 'Collapse' : 'Full view'}>
          {expanded ? <CollapseIcon /> : <ExpandIcon />}
          {expanded ? 'Collapse' : 'Full view'}
        </button>
      </div>

      <div className="thread-body">
        <div className="msg in">
          <div className="msg-head">
            <span className="msg-icon"><ReplyDown size={11} /></span>
            <span className="msg-from">{email.metadata?.fromName || email.from_address}</span>
            <span className="msg-when">{new Date(email.sent_at).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}</span>
          </div>
          {email.subject && <div className="msg-subject">{email.subject}</div>}
          {email.metadata?.body_text
            ? <MessageBody text={email.metadata.body_text} />
            : <div className="msg-nobody">No body preview available.</div>}
          <AttachmentChips items={email.attachments} />
        </div>
      </div>

      <div className="thread-reply">
        <div className="thread-reply-head">
          <div className="thread-reply-label">Assign to a host · <span>it then routes to that host’s program (shared if multi-program)</span></div>
        </div>
        <div className="inbox-search" style={{ margin: '4px 0 8px' }}>
          <SearchIcon />
          <input placeholder="Search a host to assign…" value={q} onChange={e => setQ(e.target.value)} autoFocus />
        </div>
        {err && <div className="thread-reply-err">{err}</div>}
        {results.length > 0 && (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
            {results.map(h => (
              <li key={h.id}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--line-2)', borderRadius: 8 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{h.org_name}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{(h.programs_accepted || []).join(' + ') || 'no programs'}{h.city ? ` · ${h.city}` : ''}</div>
                </div>
                <button className="thread-send" disabled={assigning} onClick={() => assign(h.id)}>
                  {assigning ? '…' : 'Assign'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
